import { useCallback, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  CfarContractStatus,
  PortalItineraryStatus,
  type BookedFlightItineraryWithDepartureTime,
} from "@b2bportal/air-booking-api";
import {
  FlightPostBookingSelectors,
  FlightPostBookingHooks,
} from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import { BookingDetailsSection } from "./BookingDetailsSection";
import { FlightDetailsPanel } from "./FlightDetailsPanel";
import { FlightExtrasSection } from "./FlightExtrasSection";
import { FlightScheduleChangeComparisonPanel } from "./FlightScheduleChangeComparisonPanel";
import { FlightTripDetailPriceBreakdown } from "./FlightTripDetailPriceBreakdown";
import { FlightTripHeader } from "./FlightTripHeader";
import styles from "./TripDetailContent.module.scss";
import { FlightInsuranceCancellationBanner, ManageBooking } from "@/components";
import { MyTripPageHeader } from "@/overrides/apac/apac-ui/components/MyTripPageHeader/MyTripPageHeader";
import { hasFlightInsurance } from "@/utilities/insurance";

export interface FlightItineraryDetailContext {
  itinerary: BookedFlightItineraryWithDepartureTime;
}

interface TripDetailContentProps {
  context: FlightItineraryDetailContext;
  onQueryCancellation: () => void;
  onQueryExchange: () => void;
  children?: ReactNode;
}

export const TripDetailContent = ({
  context,
  onQueryCancellation,
  onQueryExchange,
  children,
}: TripDetailContentProps) => {
  const [, cn] = useModuleBEM(styles, "TripDetailContent");
  const navigate = useNavigate();
  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const airlines = useSelector(FlightPostBookingSelectors.getAirlines);

  const onCfarExercise = useCallback(() => {
    navigate("cancel-for-any-reason");
  }, [navigate]);

  const isCfarExercised =
    context.itinerary.cfar?.status === CfarContractStatus.Exercised;

  const status = context.itinerary.status;

  const isActiveBooking =
    !isCfarExercised &&
    status !== PortalItineraryStatus.Canceled &&
    status !== PortalItineraryStatus.Pending;

  // Check for schedule changes
  const scheduleChangeData = FlightPostBookingHooks.useScheduleChange(
    context.itinerary
  );
  const hasScheduleChange = scheduleChangeData?.context?.diffModel != null;

  const isCancelled = status === PortalItineraryStatus.Canceled;
  const showInsuranceCancellationBanner =
    isCancelled && hasFlightInsurance(context.itinerary);

  return (
    <>
      <MyTripPageHeader className={cn("page-header")} />

      <div className={cn("card-wrapper")}>
        {showInsuranceCancellationBanner && (
          <FlightInsuranceCancellationBanner />
        )}
        <FlightTripHeader
          itinerary={context.itinerary}
          airportMap={airports}
          isCfarExercised={isCfarExercised}
        />

        <div className={cn("two-column")}>
          <div className={cn("left-column")}>
            {hasScheduleChange ? (
              <FlightScheduleChangeComparisonPanel
                className={cn("comparison-panel")}
                itinerary={context.itinerary}
                diffModel={scheduleChangeData.context.diffModel}
              />
            ) : (
              <FlightDetailsPanel
                className={cn("flight-details-panel")}
                itinerary={context.itinerary}
                airportMap={airports}
                airlineMap={airlines}
              />
            )}
            <BookingDetailsSection
              className={cn("booking-details")}
              itinerary={context.itinerary}
            />
            <FlightExtrasSection
              className={cn("extras-section")}
              itinerary={context.itinerary}
              airportMap={airports}
              airlineMap={airlines}
            />
            <FlightTripDetailPriceBreakdown
              className={cn("price-breakdown")}
              itinerary={context.itinerary}
            />
            {children}
          </div>

          <ManageBooking
            className={cn("manage-booking")}
            variant={ProductType.FLIGHTS}
            itinerary={context.itinerary}
            isActiveBooking={isActiveBooking}
            onQueryCancellation={onQueryCancellation}
            onQueryExchange={onQueryExchange}
            onCfarExercise={onCfarExercise}
          />
        </div>
        {children}
      </div>
    </>
  );
};
